import { ref } from 'vue';

/**
 * Composable for handling modal overlay clicks properly.
 * 
 * This prevents accidental modal closing when user starts selecting text
 * inside the modal and releases the mouse outside (on the overlay).
 * 
 * The modal will only close if BOTH mousedown AND mouseup happen on the overlay element.
 * 
 * Usage:
 * ```vue
 * <template>
 *   <div 
 *     class="modal-overlay" 
 *     @mousedown="handleOverlayMouseDown"
 *     @mouseup="handleOverlayMouseUp"
 *   >
 *     <div class="modal-content">...</div>
 *   </div>
 * </template>
 * 
 * <script setup>
 * import { useModalOverlay } from '@/composables/useModalOverlay';
 * const { handleOverlayMouseDown, handleOverlayMouseUp } = useModalOverlay(() => emit('close'));
 * </script>
 * ```
 */
export function useModalOverlay(onClose: () => void) {
    const mouseDownOnOverlay = ref(false);

    /**
     * Track if mousedown started on the overlay itself (not on modal content)
     */
    function handleOverlayMouseDown(event: MouseEvent) {
        // Only track if the click was directly on the overlay (not bubbled from children)
        if (event.target === event.currentTarget) {
            mouseDownOnOverlay.value = true;
        } else {
            mouseDownOnOverlay.value = false;
        }
    }

    /**
     * Close modal only if both mousedown and mouseup happened on the overlay
     */
    function handleOverlayMouseUp(event: MouseEvent) {
        if (mouseDownOnOverlay.value && event.target === event.currentTarget) {
            onClose();
        }
        // Reset the flag
        mouseDownOnOverlay.value = false;
    }

    return {
        handleOverlayMouseDown,
        handleOverlayMouseUp,
    };
}
